import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { useAuth } from "@/hooks/useAuth";
import { ArrowLeft, ArrowRight, RefreshCw, Brain, Calendar, BarChart3 } from "lucide-react";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";
import { ArticleTOC } from "@/components/resources/ArticleTOC";


const sections = [
  { id: "definition", title: "Qu'est-ce que la répétition espacée ?" },
  { id: "algorithme-sm2", title: "L'algorithme SM-2 expliqué" },
  { id: "apport-ia", title: "Comment l'IA améliore la répétition espacée" },
  { id: "methode-pratique", title: "Méthode pratique avec l'IA" },
  { id: "comparaison", title: "Répétition espacée IA vs traditionnelle" },
  { id: "points-cles", title: "Points clés" },
];

export default function AISpacedRepetitionGuideFr() {
  const { user } = useAuth();
  useSEO({
    title: "Répétition espacée avec l'IA : comment ça marche | System Atlas",
    description: "Comment l'IA améliore la répétition espacée avec planification automatique (SM-2), quiz adaptatifs et analyse des lacunes. Guide complet pour la rétention à long terme.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/ai-spaced-repetition" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/ai-spaced-repetition" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/ai-spaced-repetition" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="Répétition espacée avec l'IA : comment ça marche" description="Guide complet sur la répétition espacée augmentée par l'IA avec algorithme SM-2." publishedDate="2026-03-06" modifiedDate="2026-03-06" inLanguage="fr" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/fr/learn" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"><ArrowLeft className="w-4 h-4" />Retour aux guides</Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4 tracking-tight">Répétition espacée avec l'IA : comment ça marche</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">La répétition espacée est la technique la plus validée scientifiquement pour la mémoire à long terme. L'IA la rend pratique en automatisant la planification, en générant le contenu de révision et en identifiant ce que vous devez vraiment revoir.</p>

          <ArticleTOC sections={sections} />

          <section id="definition" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><Brain className="w-5 h-5 text-primary" />Qu'est-ce que la répétition espacée ?</h2>
            <p className="text-muted-foreground mb-4">La répétition espacée est une technique où vous révisez le contenu à des <strong>intervalles croissants</strong> dans le temps. Au lieu de tout bachoter la veille, vous révisez chaque concept juste avant de l'oublier.</p>
            <p className="text-muted-foreground mb-4">La science est claire : espacer ses révisions améliore dramatiquement la rétention à long terme par rapport au bachotage. Ce principe est basé sur la <strong>courbe de l'oubli</strong> d'Hermann Ebbinghaus (1880).</p>
            <div className="p-4 bg-muted/30 border border-border/60 rounded-lg text-sm text-muted-foreground">
              <strong>Exemple d'intervalles :</strong> Révision après 1 jour → 3 jours → 7 jours → 14 jours → 30 jours. Si vous vous trompez, l'intervalle revient à un plus court.
            </div>
          </section>

          <section id="algorithme-sm2" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-primary" />L'algorithme SM-2 expliqué</h2>
            <p className="text-muted-foreground mb-4">SM-2 (SuperMemo 2) est l'algorithme qui alimente la plupart des systèmes de répétition espacée, dont Anki et System Atlas :</p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Chaque élément a un <strong>facteur de facilité</strong> (départ à 2,5)</li>
              <li>• Après chaque révision, vous évaluez la qualité de votre rappel (échelle 0–5)</li>
              <li>• <strong>Bon rappel</strong> → l'intervalle augmente, le facteur s'ajuste à la hausse</li>
              <li>• <strong>Mauvais rappel</strong> → l'intervalle revient à 1 jour, le facteur baisse</li>
              <li>• L'algorithme calcule la <strong>date optimale de prochaine révision</strong></li>
            </ul>
            <p className="text-muted-foreground mt-4 text-sm">Résultat : vous passez plus de temps sur ce qui est difficile et moins sur ce que vous maîtrisez déjà.</p>
          </section>

          <section id="apport-ia" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" />Comment l'IA améliore la répétition espacée</h2>
            <p className="text-muted-foreground mb-4">La répétition espacée traditionnelle exige de créer ses propres cartes. L'IA supprime cet obstacle :</p>
            <div className="space-y-3">
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Génération automatique du contenu</h3>
                <p className="text-sm text-muted-foreground">Uploadez un PDF → obtenez fiche + quiz. Pas de création manuelle de cartes.</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Planification pilotée par les quiz</h3>
                <p className="text-sm text-muted-foreground">Vos scores alimentent directement le SM-2. Les fiches bien maîtrisées sont espacées ; les faibles reviennent plus vite.</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Intégration de l'analyse des lacunes</h3>
                <p className="text-sm text-muted-foreground">Après chaque quiz, l'IA identifie quels concepts spécifiques revoir. pas juste « revoir cette fiche ».</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Génération de plan d'étude</h3>
                <p className="text-sm text-muted-foreground">Un plan quotidien montre exactement quelles fiches sont à réviser et lesquelles sont prioritaires.</p>
              </div>
            </div>
          </section>

          <section id="methode-pratique" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" />Méthode pratique avec l'IA</h2>
            <ol className="space-y-3 text-muted-foreground list-decimal list-inside">
              <li><strong>Uploadez vos supports de cours</strong> et générez des fiches de révision</li>
              <li><strong>Passez le premier quiz</strong>. cela établit votre niveau de base par concept</li>
              <li><strong>Consultez votre plan d'étude</strong> quotidiennement. il montre ce qui est à réviser</li>
              <li><strong>Révisez la fiche</strong> pour les éléments dus, puis repassez le quiz</li>
              <li><strong>Votre score met à jour le planning SM-2</strong> automatiquement</li>
              <li><strong>Avec le temps</strong>, les concepts maîtrisés passent à des révisions mensuelles tandis que les faibles reçoivent une attention fréquente</li>
            </ol>
          </section>

          <section id="comparaison" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Répétition espacée IA vs traditionnelle</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-border/60 rounded-lg">
                <thead><tr className="bg-muted/30"><th className="text-left p-3 font-medium">Aspect</th><th className="text-left p-3 font-medium">Traditionnelle (Anki)</th><th className="text-left p-3 font-medium">IA (Atlas)</th></tr></thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t border-border/40"><td className="p-3">Création du contenu</td><td className="p-3">Écriture manuelle de flashcards</td><td className="p-3">Auto-généré depuis vos PDF</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Format de révision</td><td className="p-3">Flashcards individuelles</td><td className="p-3">Fiches structurées + quiz QCM</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Planification</td><td className="p-3">SM-2 par carte</td><td className="p-3">SM-2 par fiche + analyse des lacunes</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Temps de mise en place</td><td className="p-3">Heures</td><td className="p-3">Secondes</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Détection des faiblesses</td><td className="p-3">Implicite (taux d'échec)</td><td className="p-3">Explicite (analyse IA)</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="points-cles" className="mb-10 p-6 bg-muted/30 border border-border/60 rounded-xl">
            <h2 className="text-xl font-semibold mb-3">Points clés</h2>
            <ul className="space-y-2 text-muted-foreground text-sm">
              <li>• La répétition espacée est la méthode la plus validée pour la mémoire à long terme</li>
              <li>• SM-2 calcule les intervalles de révision optimaux basés sur vos performances</li>
              <li>• L'IA supprime le plus gros obstacle (création manuelle) et ajoute l'analyse des lacunes</li>
              <li>• La combinaison quiz auto-générés + planification SM-2 + analyse des lacunes crée un système de rétention complet</li>
            </ul>
          </section>
        </article>

        {!user && (
          <section className="text-center p-8 bg-primary/5 border border-primary/15 rounded-xl mb-10">
            <h2 className="text-lg font-medium mb-2">Commencez la répétition espacée IA</h2>
            <p className="text-sm text-muted-foreground mb-6">Uploadez un PDF et obtenez votre premier plan de révision IA. gratuit.</p>
            <Link to="/"><button className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">Commencer gratuitement <ArrowRight className="w-4 h-4" /></button></Link>
          </section>
        )}

        <nav className="flex flex-col gap-2 text-sm">
          <span className="text-muted-foreground font-medium">Guides connexes :</span>
          <Link to="/fr/learn/spaced-repetition" className="text-primary hover:underline">La répétition espacée : la science de la mémoire →</Link>
          <Link to="/fr/learn/active-recall" className="text-primary hover:underline">Le rappel actif : la science de l'apprentissage →</Link>
          <Link to="/fr/learn/ai-quiz-generator" className="text-primary hover:underline">Créer des quiz à partir de ses cours avec l'IA →</Link>
        </nav>
      </div>
    </>
  );
}
